import { useState } from 'react';
import AdminLayout from '@/components/feature/AdminLayout';
import { ErrorState } from '@/components/feature/AsyncState';
import { useSuppliers } from '@/hooks/useSuppliers';
import SupplierModal from './components/SupplierModal';
import SupplierDetailModal from './components/SupplierDetailModal';
import type { Supplier } from '@/services/suppliers';

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

const CREDIT_META: Record<string, { label: string; cls: string }> = {
  'Al día': { label: 'Al día', cls: 'text-emerald-700 bg-emerald-50' },
  'Pendiente de pago': { label: 'Pendiente de pago', cls: 'text-amber-700 bg-amber-50' },
  'Pago parcial': { label: 'Pago parcial', cls: 'text-orange-700 bg-orange-50' },
};

function creditStatus(raw: string): string {
  if (!raw) return 'Al día';
  return CREDIT_META[raw] ? raw : 'Al día';
}

export default function SuppliersPage() {
  const { data, loading, error, refetch } = useSuppliers();
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<Supplier | null>(null);
  const [detailFor, setDetailFor] = useState<Supplier | null>(null);

  const filtered = data.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.companyName.toLowerCase().includes(q) ||
      s.contactName.toLowerCase().includes(q) ||
      s.email.toLowerCase().includes(q)
    );
  });

  return (
    <AdminLayout>
      <div className="p-4 lg:p-6 space-y-5 max-w-[1400px] mx-auto">
        {error && <ErrorState message={error} onRetry={refetch} />}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-foreground-950">Proveedores</h1>
            <p className="text-sm text-foreground-500 mt-0.5 flex items-center flex-wrap gap-2">
              <span>{data.length} proveedores registrados</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium whitespace-nowrap">
                Supabase
              </span>
            </p>
          </div>
          <button
            onClick={() => setShowNew(true)}
            className="text-sm px-4 py-2.5 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-add-line mr-1.5"></i>Nuevo Proveedor
          </button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Proveedores activos</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{data.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Con contacto registrado</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">
              {data.filter((s) => s.contactName).length}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-background-200 p-4">
            <p className="text-xs text-foreground-500">Con correo</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">
              {data.filter((s) => s.email).length}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input
            type="text"
            placeholder="Buscar por empresa, contacto o correo..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-background-200 rounded-lg bg-white text-foreground-700 focus:outline-none focus:ring-2 focus:ring-accent-300"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-background-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-100 text-left bg-background-50">
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Empresa</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Contacto</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Teléfono</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Email</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Estado</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500">Alta</th>
                  <th className="px-5 py-3 text-xs font-medium text-foreground-500 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-10 text-center text-foreground-400">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>Cargando proveedores...
                    </td>
                  </tr>
                ) : error ? null : (
                  filtered.map((s) => (
                    <tr key={s.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center flex-shrink-0">
                            <i className="ri-truck-line text-accent-600"></i>
                          </div>
                          <p className="text-foreground-900 font-medium">{s.companyName}</p>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-foreground-700">{s.contactName || '—'}</td>
                      <td className="px-5 py-3 text-foreground-600 whitespace-nowrap">{s.phone || '—'}</td>
                      <td className="px-5 py-3 text-foreground-600">{s.email || '—'}</td>
                      <td className="px-5 py-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${CREDIT_META[creditStatus(s.creditStatus)].cls}`}>
                          {CREDIT_META[creditStatus(s.creditStatus)].label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatDate(s.createdAt)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setDetailFor(s)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            title="Ver abonos y estado"
                            aria-label="Ver abonos y estado"
                          >
                            <i className="ri-bank-card-line text-foreground-400"></i>
                          </button>
                          <button
                            onClick={() => setEditing(s)}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
                            aria-label="Editar proveedor"
                          >
                            <i className="ri-more-2-fill text-foreground-400"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-12 text-foreground-500">
              <i className="ri-truck-line text-3xl block mb-2"></i>
              <p className="text-sm">
                {data.length === 0
                  ? 'Aún no hay proveedores registrados.'
                  : 'No se encontraron proveedores con ese filtro.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {showNew && <SupplierModal onClose={() => setShowNew(false)} onSaved={refetch} />}
      {editing && (
        <SupplierModal supplier={editing} onClose={() => setEditing(null)} onSaved={refetch} />
      )}
      {detailFor && (
        <SupplierDetailModal
          supplierId={detailFor.id}
          supplierName={detailFor.companyName}
          onClose={() => setDetailFor(null)}
          onSaved={refetch}
        />
      )}
    </AdminLayout>
  );
}